import { Card, Collapse, List, Space, Typography } from 'antd';
import { format } from 'date-fns';
import type { UserShiftClashReport } from '../../../hooks/types';
import { userFullName } from '../../../lib/utils';
import { shiftDateFormat } from '../../../lib/utils/date-time';
import rfsTheme from '../../../theme';
import type { RosterUser } from '../../Roster/types';
import ShiftWarningTeamButton from './ShiftWarningTeamButton';

const { Text } = Typography;

const { Panel } = Collapse;

type ShiftClashAlertProps = {
  readonly teamName: string;
  readonly userShiftClashReport: UserShiftClashReport;
  readonly usersOnRoster: Record<string, RosterUser> | undefined;
};

const listGridLayout = {
  gutter: 16,
  xs: 1,
  sm: 2,
  md: 4,
  lg: 4,
  xl: 6,
  xxl: 3,
};

const alertColours = {
  info: '#e6f4ff',
  danger: 'rgba(222, 20, 4, 0.1)',
};

/**
 * Alert section for shift clashes of a user.
 */
const ShiftClashAlert = ({
  teamName,
  userShiftClashReport,
  usersOnRoster,
}: ShiftClashAlertProps) => {
  const teamNamesWithClashes: readonly string[] =
    userShiftClashReport.clashedShiftGroups
      .flatMap((shiftGroup) => shiftGroup.shifts)
      .map((shift) => shift.team?.name ?? '');

  const alertType = teamNamesWithClashes.includes(teamName) ? 'danger' : 'info';

  const user = usersOnRoster?.[userShiftClashReport.userId];

  if (user === undefined) {
    return <></>;
  }

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ display: 'flex', margin: '10px 0' }}
    >
      <Collapse>
        <Panel
          id={`${userShiftClashReport.userId}-shift-clash-panel`}
          style={{
            backgroundColor: alertColours[alertType],
          }}
          key={`${userShiftClashReport.userId}-shift-clashes`}
          header={
            <Text>
              <Text strong>
                {userFullName({
                  first_name: user.firstName,
                  last_name: user.lastName,
                })}
              </Text>
              &nbsp; has shift clashes,
              {alertType === 'info' ? <strong> in another team</strong> : ''}
              &nbsp; check details to review shifts.
            </Text>
          }
        >
          <List
            grid={listGridLayout}
            dataSource={[...userShiftClashReport.clashedShiftGroups]}
            renderItem={(clashedGroup, groupIndex) => (
              <List.Item>
                <Card
                  size="small"
                  title={
                    <Text style={{ color: rfsTheme.darkRfsRed }}>
                      Clashed shifts {groupIndex + 1}
                    </Text>
                  }
                  style={{ background: 'none' }}
                >
                  <Space direction="vertical">
                    {clashedGroup.shifts.map((shift, index) => (
                      <Text
                        key={`clashed-shift-${userShiftClashReport.userId}-${shift.id}-${index}`}
                      >
                        {format(new Date(shift.startTime), shiftDateFormat)}{' '}
                        as&nbsp;
                        <Text strong>{shift.teamRoleName}</Text>&nbsp;
                        <ShiftWarningTeamButton team={shift.team} />
                      </Text>
                    ))}
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </Panel>
      </Collapse>
    </Space>
  );
};

export default ShiftClashAlert;
